import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { InsightsService } from '@services/insights.service';
import { LocalStorageService, SessionStorageService } from '@services/storage.service';
import { IAccount, IUserAccessResult, UserService } from '@services/user.service';
import { ServiceItem } from '@shared/serviceitem';
import { CognitoRefreshToken, CognitoUserPool } from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import queryString from 'query-string';
import { BehaviorSubject, filter, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { IData as IDataTopbar } from '../topbar/topbar.component';

// Exported pure helpers to make mapping and filtering unit-testable
export function mapOrganizationAccount(organization: { id: string; name: string }, account: any): IAccount {
	return {
		id: account.id,
		name: account.name,
		defaultEngine: account.defaultEngine,
		organization: { id: organization.id, name: organization.name },
		allowedIPs: account.allowedIPs,
	};
}

export function accountAllowedForCaller(account: IAccount, callerIp?: string, isSuperAdmin?: boolean): boolean {
	// If allowedIPs is empty or not set, include the account
	if (!account.allowedIPs || (Array.isArray(account.allowedIPs) && account.allowedIPs.length === 0)) return true;

	// If callerIp is not provided, include the account as the allowedIPs also are checked on backend apis
	if (!callerIp || isSuperAdmin) return true;

	return account.allowedIPs.some((ip: string) => ip.trim() === callerIp);
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(
		private sessionStorageService: SessionStorageService,
		private localStorageService: LocalStorageService,
		private userService: UserService,
		private insightsService: InsightsService,
		private router: Router,
	) {}

	public loggedInStatus = new BehaviorSubject<boolean>(false);
	public isLoggedIn$ = this.loggedInStatus.asObservable();
	private lastIsAuth = false;

	isLoggedIn(): boolean {
		const poolData = {
			Storage: window.sessionStorage,
			UserPoolId: environment.cognitoUserPoolId,
			ClientId: sessionStorage.getItem('User.ClientId') ? sessionStorage.getItem('User.ClientId') : environment.cognitoAppClientId,
		};

		const userPool = new CognitoUserPool(poolData);
		const cognitoUser = userPool.getCurrentUser();

		if (cognitoUser != null) {
			cognitoUser.getSession((err: any, session: any) => {
				let isAuth = false;
				if (!err && session) {
					isAuth = session.isValid();
				}
				this.lastIsAuth = isAuth;
				this.loggedInStatus.next(isAuth);

				// Optionally refresh token if valid
				if (isAuth) {
					this.refreshToken();
					const FIVE_MINUTES = 5 * 60 * 1000;
					setInterval(() => {
						this.refreshToken();
					}, FIVE_MINUTES);
				}
				if (err) {
					// alert is allowed here
					// eslint-disable-next-line no-alert
					alert(err.message || JSON.stringify(err));
				}
			});
		} else {
			this.lastIsAuth = false;
			this.loggedInStatus.next(false);
		}

		// Return the last known value synchronously
		return this.lastIsAuth;
	}

	private refreshToken() {
		const expire: number = Number.parseInt(this.sessionStorageService.get('User.Session.Expire'));
		const now: number = Math.floor(new Date().getTime() / 1000);
		if (expire - now < 600) {
			console.log('----------------------// refreshing token \\-----------------------');
			const poolData = {
				Storage: window.sessionStorage,
				UserPoolId: environment.cognitoUserPoolId,
				ClientId: this.sessionStorageService.get('User.ClientId') ? this.sessionStorageService.get('User.ClientId') : environment.cognitoAppClientId,
			};

			const userPool = new CognitoUserPool(poolData);
			const cognitoUser = userPool.getCurrentUser();
			let refreshToken: CognitoRefreshToken = null;
			if (cognitoUser != null) {
				refreshToken = new CognitoRefreshToken({
					RefreshToken: this.sessionStorageService.get('User.RefreshToken'),
				});

				cognitoUser.refreshSession(refreshToken, (err: any, session: any) => {
					if (err) {
						console.log(`Auth refreshtoken error: ${err.code} ${err.message}`);
						return;
					}
					const identityjwtdecoded: any = jwtDecode(session.getIdToken().getJwtToken());
					this.sessionStorageService.set('User.Session.Expire', identityjwtdecoded.exp);
					this.sessionStorageService.set('User.Authorization', session.getAccessToken().getJwtToken());
				});
			}
		}
	}

	setUserPermissions(ua: IUserAccessResult) {
		const permissions: string[] = ua?.result.permissions;
		if (ua) {
			this.sessionStorageService.set('User.Permissions', JSON.stringify(permissions));
		} else {
			this.sessionStorageService.remove('User.Permissions');
		}
	}

	mapAccounts(ua: IUserAccessResult) {
		const accounts = [];
		// Caller IP used to filter accounts when allowedIPs is not empty
		const callerIp = this.sessionStorageService.get('Caller.IP');
		let isAccountAdmin: boolean = false;
		const hasAccountManager = ua.result.organizations?.some((org) => org.permissions?.includes('iam:AccountManager') || org.accounts?.some((acc) => acc.permissions?.includes('iam:AccountManager')));
		if (ua.result.permissions.length > 0 || hasAccountManager) {
			isAccountAdmin = true;
		}
		ua.result.organizations.forEach((o) => {
			o.accounts
				.map((a) => mapOrganizationAccount(o, a))
				.filter((a) => accountAllowedForCaller(a, callerIp, isAccountAdmin))
				.forEach((a) => accounts.push(a));
		});

		// For some weird reason this "of()" fails approximately 1 out of 10 times, resulting in an empty Org/Account dropdown list,
		// so I have made the subject (accountsSubject$) public and replaced
		// this.userService.accounts$ = of(accounts);
		// with:
		this.userService.accountsSubject$.next(accounts);

		if (ua?.result) {
			this.sessionStorageService.set('User.AccessMap', JSON.stringify(ua.result));
		} else {
			this.sessionStorageService.remove('User.AccessMap');
		}
	}

	setIAMUser(ua: IUserAccessResult, accounts = [], services) {
		this.sessionStorageService.set('User.IAM.Id', ua?.result.id);
		if (ua) {
			// A deep link's account (stored by AuthGuard) wins when the user has it; an unknown one falls through to the normal choice.
			const deepLinkAccountId = this.sessionStorageService.get('User.DeepLink.Account');
			this.sessionStorageService.remove('User.DeepLink.Account');
			const deepLinkAccount: IAccount | undefined = deepLinkAccountId ? accounts.find((account: IAccount) => account.id === deepLinkAccountId) : undefined;
			if (deepLinkAccount) {
				this.selectAccount(deepLinkAccount, services);
			} else if (this.localStorageService.get('User.Selected.Account')) {
				const account: IAccount = JSON.parse(this.localStorageService.get('User.Selected.Account'));
				if (account) {
					this.selectAccount(account, services);
				}
			} else if (accounts.length === 1) {
				this.selectAccount(accounts[0], services);
			} else {
				this.userService.fetchMe().subscribe((x) => {
					this.sessionStorageService.set('User.IAM.DefaultAccount', x.result.defaultAccount);
					this.setDefaultAccount(x.result.defaultAccount, services);
				});
			}
		} else {
			this.sessionStorageService.remove('User.IAM.DefaultAccount');
		}
	}

	setDefaultAccount(accountId: string, services) {
		let defaultAccount;
		this.userService.accounts$
			.pipe(
				filter((v) => v.length > 0),
				map((r) =>
					r.reduce((a, v) => {
						if (!a.find((o) => o.organization.id === v.organization.id)) {
							a.push({
								organization: v.organization,
								accounts: [],
							});
						}
						a.find((o) => o.organization.id === v.organization.id)?.accounts.push(v);

						return a;
					}, [] as IDataTopbar[]),
				),
			)
			.subscribe((x) => {
				x.find((y) => {
					defaultAccount = y.accounts.find((z) => z.id === accountId);
					if (defaultAccount) {
						const currentlySelectedAccount: IAccount = JSON.parse(this.sessionStorageService.get('User.Account'));
						if (!currentlySelectedAccount) {
							this.selectAccount(defaultAccount, services);
						}
					}
				});
			});
	}

	selectAccount(account: IAccount | null, services) {
		if (this.userService.impersonateUser) {
			this.insightsService.exitImpersonation([]); // Was "this.permissions" in topbar component, but thata array never got a value
			void this.router.navigate(['/insights/administration/users']);
		}
		this.localStorageService.set('User.Selected.Account', JSON.stringify(account));
		this.sessionStorageService.set('User.Account', JSON.stringify(account));
		this.userService.account = account;
		this.updatePermissions(account);

		if (this.sessionStorageService.get('User.InviteToken')) {
			this.sessionStorageService.remove('User.InviteToken');
		} else {
			const userPermissions = JSON.parse(this.sessionStorageService.get('User.Permissions'));
			if (this.sessionStorageService.get('User.Permissions')) {
				const enabledServices: any[] = services.filter((item) => {
					if (item !== null && item.label.toLowerCase() === 'sales') {
						return this.sessionStorageService.get('User.Email')?.toLowerCase().includes('@acies.dk') || this.sessionStorageService.get('User.Email')?.toLowerCase().includes('@elitfonster.se');
					}
					if (item !== null && item.label.toLowerCase() === 'configurator') {
						return true;
					}
					const permissions = item.permission.split('|');
					return (
						item.enabled === true &&
						(item.permission === '' ||
							permissions.some((permission) => {
								return userPermissions.includes(permission);
							}))
					);
				});
				if (this.localStorageService.get('User.DeepLink')?.length > 0) {
					const linkParams = this.localStorageService.get('User.DeepLink').split('?');
					const link = linkParams[0].startsWith('/') ? linkParams[0].substring(1).split('/')[0] : linkParams[0].split('/')[0];
					const linkService = enabledServices.filter((item) => item.link.startsWith(link));
					this.localStorageService.remove('User.DeepLink');
					this.sessionStorageService.set('Navigation.DeepLink', 'x');
					const serviceItem: ServiceItem | undefined = linkService[0];
					if (serviceItem) {
						let queryParams = {};
						if (linkParams[1]) {
							queryParams = queryString.parse(linkParams[1]);
						}
						this.sessionStorageService.set('Selected.ServiceName', serviceItem.forceTranslateKey);
						this.localStorageService.set('User.Selected.ServiceName', serviceItem.label);
						void this.router.navigate([linkParams[0].startsWith('/') ? linkParams[0].substring(1) : linkParams[0]], { queryParams });
					} else {
						void this.router.navigate(['core']);
					}
				} else {
					if (this.sessionStorageService.get('Navigation.DeepLink') === null || this.sessionStorageService.get('Navigation.DeepLink').length === 0) {
						const prevSelectedService: any = enabledServices.find((service) => service.label === localStorage.getItem('User.Selected.ServiceName'));
						if (prevSelectedService != null && prevSelectedService !== undefined) {
							this.navigateToService(prevSelectedService);
						} else if (enabledServices.length === 1) {
							this.navigateToService(enabledServices[0]);
						} else {
							const redirectService: ServiceItem = JSON.parse(this.sessionStorageService.get('RedirectToService'));
							if (redirectService) {
								this.sessionStorageService.remove('RedirectToService');
								this.sessionStorageService.set('Selected.ServiceName', redirectService.forceTranslateKey);
								this.localStorageService.set('User.Selected.ServiceName', redirectService.label);
								void this.router.navigate([redirectService.link]);
							}
						}
					}
					sessionStorage.removeItem('Link.Illegal');
					const url = new URL(window.location.href);
					url.searchParams.delete('accountId');
					window.history.replaceState({}, '', url.toString());
				}
			}
		}
	}

	navigateToService(service: any) {
		if (!sessionStorage.getItem('Link.Illegal') && !this.sessionStorageService.get('Selected.ServiceName')) {
			if (service.label) {
				this.sessionStorageService.set('Selected.ServiceName', service.forceTranslateKey);
				this.localStorageService.set('User.Selected.ServiceName', service.label);
			}
			void this.router.navigate([service.link]);
		}
	}

	updatePermissions(account: IAccount) {
		const accessMap = JSON.parse(this.sessionStorageService.get('User.AccessMap'));
		if (accessMap) {
			let permissions: string[] = accessMap.permissions as string[];
			const org = accessMap.organizations.find((x) => x.name === account.organization.name);
			if (org) {
				permissions = permissions.concat(org.permissions as string[]);
				const acc = org.accounts.find((x) => x.name === account.name);
				if (acc) {
					permissions = permissions.concat(acc.permissions as string[]);
				}
			}
			this.sessionStorageService.set('User.Permissions', JSON.stringify(permissions));
		} else {
			this.sessionStorageService.remove('User.Permissions');
		}
	}
}
